import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
// Constants - Functions
import { parseSecondsToMinutes, renderToolTip } from "../../../../../../../../config/functions";
import { isMotorProtocol, isCognitiveProtocol } from "../../../../../../../../config/validateProtocols";
import { GetAppBasicProtocolData } from "../../../../../../../prescriptions/sections/getProtocolData/appBasics";
import { GetAppRotationProtocolData } from "../../../../../../../prescriptions/sections/getProtocolData/appRotation";
import { GetAppARProtocolData } from "../../../../../../../prescriptions/sections/getProtocolData/appAR";
import { GetHandTrackingProtocolData } from "../../../../../../../prescriptions/sections/getProtocolData/webHandTracking";
import { GetFullBodyProtocolData } from "../../../../../../../prescriptions/sections/getProtocolData/webFullBody";
// Components
import {
  PrescriptionDiv,
  ProtocolCardDiv,
  VideoButton,
  ProtocolImgDiv,
  ProtocolImg,
  VideoButtonImg,
  InfoImg,
  ProtocolName,
  PrescriptionDaysDiv,
  PrescriptionDaysAligner,
  DayText,
  InputCheckboxDiv,
  InputCheckbox,
  FunctionsDiv,
  FunctionDiv,
  FunctionText,
  DurationDiv,
  DurationSelect,
} from "./styles";
import ProtocolVideoPopup from "../../../../../../../protocolVideoPopup";
// Assets
import IcoOpenVideo from "../../../../../../../../assets/icons/ico_video.svg";
import IcoInfo from "../../../../../../../../assets/icons/ico_info_blue.svg";

const ProtocolPrescription = ({
  protocolId,
  prescriptionDuration,
  prescriptionDays = [],
  prescriptionStartingDate,
  prescriptionEndingDate,
  prescriptionsToDatabase,
}) => {
  const [protocolImg, setProtocolImg] = useState(null);
  const [protocolName, setProtocolName] = useState(null);
  const [protocolDescription, setProtocolDescription] = useState(null);
  const [protocolVideo, setProtocolVideo] = useState(null);
  // Days
  const [monday, setMonday] = useState(false);
  const [tuesday, setTuesday] = useState(false);
  const [wednesday, setWednesday] = useState(false);
  const [thursday, setThursday] = useState(false);
  const [friday, setFriday] = useState(false);
  const [saturday, setSaturday] = useState(false);
  const [sunday, setSunday] = useState(false);
  const [duration, setDuration] = useState(parseSecondsToMinutes(prescriptionDuration));
  const [minDurationLimitPerDay] = useState(1200);
  const [maxDurationLimitPerDay] = useState(1800);
  //

  const [isProtocolVideoShowed, setIsProtocolVideoShowed] = useState(false);
  const [t] = useTranslation("global");

  // Setting prescription days
  useEffect(() => {
    const daysToSet = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

    const setDays = (weekday, setDay) => {
      const filteredDay = prescriptionDays.filter((prescriptionDay) => prescriptionDay === weekday);
      if (filteredDay.length) setDay(true);
    };

    daysToSet.forEach((day, index) => {
      setDays(day, [setMonday, setTuesday, setWednesday, setThursday, setFriday, setSaturday, setSunday][index]);
    });
  }, [prescriptionDays]);

  // Setting protocol info
  useEffect(() => {
    const getProtocolDataFunctions = [
      GetAppBasicProtocolData,
      GetAppRotationProtocolData,
      GetAppARProtocolData,
      GetHandTrackingProtocolData,
      GetFullBodyProtocolData,
    ];

    for (const getProtocolData of getProtocolDataFunctions) {
      const protocolData = getProtocolData(protocolId);
      if (protocolData) {
        setProtocolImg(protocolData.img);
        setProtocolName(t(`protocols_${protocolData.name}`));
        setProtocolDescription(t(`protocols_${protocolData.description}`));
        setProtocolVideo(t(`protocols_${protocolData.video}`));
      }
    }
  }, [protocolId, t]);

  const isSomeDayActive = () => {
    return ![monday, tuesday, wednesday, thursday, friday, saturday, sunday].some(Boolean);
  };

  const getCurrentTimePrescription = (day) => {
    return prescriptionsToDatabase
      .filter((prescription) => prescription.WEEKDAY === day && prescription.STARTING_DATE)
      .reduce((accumulator, prescription) => accumulator + Number(prescription.SESSION_DURATION), 0);
  };

  const getCurrentTimePrescriptionWithoutCurrentActivity = (day) => {
    return prescriptionsToDatabase
      .filter((prescription) => prescription.WEEKDAY === day && prescription.STARTING_DATE && prescription.PROTOCOL_ID !== protocolId)
      .reduce((accumulator, prescription) => accumulator + Number(prescription.SESSION_DURATION), 0);
  };

  const exceedsTheMinimumDurationPerCheckbox = (day) => {
    const prescribedTimeOfDay = getCurrentTimePrescription(day) - duration * 60;
    return prescribedTimeOfDay < minDurationLimitPerDay;
  };

  const exceedsTheMaximumDurationPerCheckbox = (day) => {
    const prescribedTimeOfDay = getCurrentTimePrescription(day) + duration * 60;
    return prescribedTimeOfDay > maxDurationLimitPerDay;
  };

  const exceedsTheMinimumDuration = (day, min) => {
    const prescribedTimeOfDay = getCurrentTimePrescriptionWithoutCurrentActivity(day) + Number(min) * 60;
    return prescribedTimeOfDay < minDurationLimitPerDay;
  };

  const exceedsTheMaximumDuration = (day, min) => {
    const prescribedTimeOfDay = getCurrentTimePrescriptionWithoutCurrentActivity(day) + Number(min) * 60;
    return prescribedTimeOfDay > maxDurationLimitPerDay;
  };

  const handleSetCheckbox = (setDay, isActiveDay, day) => {
    if (isActiveDay && exceedsTheMinimumDurationPerCheckbox(day)) {
      return toast.error("Less than 20 minutes per day cannot be prescribed");
    }
    if (!isActiveDay && exceedsTheMaximumDurationPerCheckbox(day)) {
      return toast.error("No more than 30 minutes per day can be prescribed");
    }
    setDay(!isActiveDay);
    const verifyPrescription = prescriptionsToDatabase.find(
      (prescription) => prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day
    );
    if (!verifyPrescription && !isActiveDay) {
      let prescription = {
        PROTOCOL_ID: protocolId,
        WEEKDAY: day,
        STARTING_DATE: prescriptionStartingDate,
        ENDING_DATE: prescriptionEndingDate,
        SESSION_DURATION: Number(duration) * 60,
      };
      return prescriptionsToDatabase.push(prescription);
    }
    if (!verifyPrescription && isActiveDay) {
      return prescriptionsToDatabase.push({
        PROTOCOL_ID: protocolId,
        WEEKDAY: day,
      });
    }
    if (verifyPrescription && !isActiveDay) {
      const prescriptionToRemove = prescriptionsToDatabase.findIndex(
        (prescription) => prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day
      );
      prescriptionsToDatabase.splice(prescriptionToRemove, 1);
      let prescription = {
        PROTOCOL_ID: protocolId,
        WEEKDAY: day,
        STARTING_DATE: prescriptionStartingDate,
        ENDING_DATE: prescriptionEndingDate,
        SESSION_DURATION: Number(duration) * 60,
      };
      return prescriptionsToDatabase.push(prescription);
    }
    if (verifyPrescription && isActiveDay) {
      prescriptionsToDatabase.forEach((prescription) => {
        if (prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day) {
          delete prescription.STARTING_DATE;
          delete prescription.ENDING_DATE;
          delete prescription.SESSION_DURATION;
        }
      });
    }
  };

  const renderProtocolCard = () => {
    return (
      <ProtocolCardDiv>
        <VideoButton onClick={() => setIsProtocolVideoShowed(true)} disabled={!protocolVideo}>
          <ProtocolImgDiv height={isSomeDayActive() ? "40px" : "38px"}>
            <ProtocolImg src={protocolImg} alt="protocol-img" />
          </ProtocolImgDiv>
          {protocolVideo ? (
            <VideoButtonImg
              src={IcoOpenVideo}
              alt="ico-video"
              data-for={`protocolDescription_${protocolId}`}
              data-tip={protocolDescription}
            />
          ) : (
            <InfoImg src={IcoInfo} alt="ico-info" data-for={`protocolDescription_${protocolId}`} data-tip={protocolDescription} />
          )}
          {renderToolTip(`protocolDescription_${protocolId}`)}
          <ProtocolName>{protocolName}</ProtocolName>
        </VideoButton>
        {isProtocolVideoShowed && <ProtocolVideoPopup setIsProtocolVideoShowed={setIsProtocolVideoShowed} protocolId={protocolId} />}
      </ProtocolCardDiv>
    );
  };

  const renderPrescriptionDays = () => {
    const days = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

    const dayValues = {
      MONDAY: [setMonday, monday],
      TUESDAY: [setTuesday, tuesday],
      WEDNESDAY: [setWednesday, wednesday],
      THURSDAY: [setThursday, thursday],
      FRIDAY: [setFriday, friday],
      SATURDAY: [setSaturday, saturday],
      SUNDAY: [setSunday, sunday],
    };

    return (
      <PrescriptionDaysDiv>
        <PrescriptionDaysAligner>
          {days.map((day) => {
            const [daySetter, dayValue] = dayValues[day];
            const id = `${day}${protocolId}`;

            const getBorderRadius = () => {
              if (day === "MONDAY") return "30px 0 0 30px";
              if (day === "SUNDAY") return "0 30px 30px 0";
              return "0";
            };

            return (
              <InputCheckboxDiv key={id} borderRadius={getBorderRadius()} background={dayValue ? "#39B54A" : "#E6E6E6"}>
                <InputCheckbox id={id} type="checkbox" value={dayValue} onClick={() => handleSetCheckbox(daySetter, dayValue, day)} />
                <DayText color={dayValue ? "#FFF" : "#808080"}>{t(`prescriptions_tableProtocolDays_${day.toLowerCase()}`)}</DayText>
              </InputCheckboxDiv>
            );
          })}
        </PrescriptionDaysAligner>
      </PrescriptionDaysDiv>
    );
  };

  const renderProtocolFunctions = () => {
    return (
      <FunctionsDiv>
        {isMotorProtocol(protocolId) && (
          <FunctionDiv background="#D3F0E3">
            <FunctionText color="#22B573">MOTOR</FunctionText>
          </FunctionDiv>
        )}
        {isCognitiveProtocol(protocolId) && (
          <FunctionDiv background="#FCDED3">
            <FunctionText color="#F15A24">COGNITIVE</FunctionText>
          </FunctionDiv>
        )}
      </FunctionsDiv>
    );
  };

  const renderDurationOptions = () => {
    const minutes = Array.from({ length: 3 }, (_, i) => {
      const min = i + 3;
      return (
        <option key={min} value={min}>
          {min} min
        </option>
      );
    });

    const handleSetDuration = (min) => {
      let activeDays = [];

      if (monday) activeDays.push("MONDAY");
      if (tuesday) activeDays.push("TUESDAY");
      if (wednesday) activeDays.push("WEDNESDAY");
      if (thursday) activeDays.push("THURSDAY");
      if (friday) activeDays.push("FRIDAY");
      if (saturday) activeDays.push("SATURDAY");
      if (sunday) activeDays.push("SUNDAY");

      const isInvalidTimePrescription = () => {
        let invalidTimePrescription = false;
        for (const day of activeDays) {
          if (exceedsTheMinimumDuration(day, min)) {
            invalidTimePrescription = true;
            toast.error("Less than 20 minutes per day cannot be prescribed");
            break;
          }
          if (exceedsTheMaximumDuration(day, min)) {
            invalidTimePrescription = true;
            toast.error("No more than 30 minutes per day can be prescribed");
            break;
          }
        }

        return invalidTimePrescription;
      };

      if (isInvalidTimePrescription()) return;

      setDuration(min);

      for (const day of activeDays) {
        const verifyPrescription = prescriptionsToDatabase.find(
          (prescription) => prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day
        );

        if (verifyPrescription) {
          prescriptionsToDatabase.forEach((prescription) => {
            if (prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day) {
              prescription.SESSION_DURATION = Number(min) * 60;
            }
          });
        } else {
          const prescription = {
            PROTOCOL_ID: protocolId,
            WEEKDAY: day,
            STARTING_DATE: prescriptionStartingDate,
            ENDING_DATE: prescriptionEndingDate,
            SESSION_DURATION: Number(min) * 60,
          };
          prescriptionsToDatabase.push(prescription);
        }
      }
    };

    return (
      <DurationDiv>
        <DurationSelect value={duration} onChange={(e) => handleSetDuration(e.target.value)} disabled={isSomeDayActive()}>
          {minutes}
        </DurationSelect>
      </DurationDiv>
    );
  };

  const getBorder = () => {
    if (!isSomeDayActive()) return "1.5px solid #26A9B6";
    return "";
  };

  const getBackgroundColor = () => {
    if (!isSomeDayActive()) return "#e5f5f6";
    return "#fff";
  };

  return (
    <PrescriptionDiv border={getBorder()} background={getBackgroundColor()}>
      {renderProtocolCard()}
      {renderPrescriptionDays()}
      {renderProtocolFunctions()}
      {renderDurationOptions()}
    </PrescriptionDiv>
  );
};

export default ProtocolPrescription;
